/* eslint-disable no-param-reassign */
import {
  ActionReducerMapBuilder,
  createAsyncThunk,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';

import { filterType } from '../../config/enum';
import DefaultApiParameters from '../../enum/DefaultApiParameters';
import {
  APIConsumerChargePersonalResponseInterface,
  ConsumerChargePersonalInitialProperties,
  RequestConsumerChargePersonalInterFace,
  ConsumerChargePersonalInterFace,
} from '../../interface/end-consumer-charge-session-personal-interface';
import { CommonFilterProperties, FilterUpdateProperties } from '../../interface/filter.interface';
import {
  ClearFilters,
  ConfigureListFilterResponse,
  RemoveSingleFilterFromList,
  UpdateCommonFilter,
} from '../../shared-functions/CommonFilters';
import { removeEmptyKeys } from '../../shared-functions/DateTime';
import endConsumerApiV2 from '../../utils/api/end-consumer-v2';

import { setNotificationMessage } from './NotificationManagementSlice';

export const fetchConsumerChargePersonal = createAsyncThunk<
  APIConsumerChargePersonalResponseInterface,
  RequestConsumerChargePersonalInterFace
>(
  'end-consumers/fetchConsumerChargePersonal',
  async (data: RequestConsumerChargePersonalInterFace, { dispatch }) => {
    const { userCustomId, ...query } = data;
    if (!userCustomId) {
      throw new Error('userCustomId is required');
    }
    const response = await endConsumerApiV2
      .fetchChargeSessionList(userCustomId, removeEmptyKeys(query))
      .then((response1) => response1)
      .catch((error) =>
        dispatch(
          setNotificationMessage({
            type: 'error',
            message: error?.response?.data?.message,
            code: error?.response?.data?.code,
            status: true,
          }),
        ),
      );
    return {
      ...response.data,
      results: response.data.results.map((consumerData: ConsumerChargePersonalInterFace) => ({
        ...consumerData,
        paymentMethod:
          consumerData?.paymentMethod === 'hyperpay' ? 'Credit Card' : consumerData?.paymentMethod,
        chargingPointSn: consumerData?.chargingPoint?.chargingPointSn,
        chargingPointName: consumerData?.chargingPoint?.chargingPointName,
        chargingStationName: consumerData?.chargingStation?.name,
        chargingStationId: consumerData?.chargingStation?._id,
        vehicleName: consumerData?.vehicle?.manufacturer,
        vehicleModel: consumerData?.vehicle?.model,
        hyperpayTransactionId: consumerData?.hyperpayTransaction?.hyperpayTransactionId,
        hyperpayTransactionIdStatus: consumerData?.hyperpayTransaction?.status,
      })),
    };
  },
);

export const fetchConsumerChargePersonalFilter = createAsyncThunk<CommonFilterProperties[], void>(
  'end-consumers/fetchConsumerChargePersonalFilter',
  async (_, { dispatch, getState }) => {
    const state: any = getState();
    const { selectedEndConsumer } = state.endConsumerType;

    if (!selectedEndConsumer?.userCustomId) {
      throw new Error('accountCustomId is required');
    }
    try {
      const response = await endConsumerApiV2.fetchChargeSessionPersonalFilters(
        selectedEndConsumer.userCustomId,
      );
      const updatedResponse: CommonFilterProperties[] = ConfigureListFilterResponse(response.data);
      return updatedResponse;
    } catch (error: any) {
      dispatch({
        type: 'notification/setNotificationMessage',
        payload: {
          type: 'error',
          message: error?.response?.data?.message || 200,
          code: error?.response?.data?.code || 'Something went wrong!',
          status: true,
        },
      });
      throw error;
    }
  },
);

const availableFilters: CommonFilterProperties[] = [
  {
    name: 'Search here...',
    key: 'searchBy',
    type: filterType.TEXT,
    value: '',
    applyFilter: false,
    enable: true,
    searchBy: true,
  },
];

// Initial state
const initialState: ConsumerChargePersonalInitialProperties = {
  consumers: [],
  limit: DefaultApiParameters.limit,
  page: DefaultApiParameters.page,
  totalPages: 0,
  totalResults: 0,
  loading: false,
  filter: {},
  filters: availableFilters,
  filterListLoading: false,
  isFilterUpdated: false,
  sortBy: '',
  sortField: '',
};

export const ConsumerChargePersonal = createSlice({
  name: 'end-consumers-charge-rersonal',
  initialState,
  reducers: {
    /**
     * Update By month filters
     * @param state
     * @param action
     */
    updateConsumerChargePersonalListFilter: (
      state,
      action: PayloadAction<FilterUpdateProperties>,
    ) => {
      const payload = { ...action.payload };
      if (payload.type === 'add') {
        const { updatedCommonFilter, isFilterUpdated } = UpdateCommonFilter(
          state.filters,
          action.payload,
        );
        state.filters = updatedCommonFilter;
        state.isFilterUpdated = isFilterUpdated;
      } else if (payload.type === 'remove') {
        state.filters = RemoveSingleFilterFromList(state.filters, payload.key);
        state.isFilterUpdated = true;
      } else if (payload.type === 'clear') {
        state.filters = ClearFilters(state.filters);
        state.isFilterUpdated = true;
      }
    },
    /**
     * function for set sortBy
     * @param state
     * @param action
     */
    setSortData: (state, action: PayloadAction<{ sortField: string; sortBy: 'asc' | 'desc' }>) => {
      state.sortField = action.payload.sortField;
      state.sortBy = action.payload.sortBy;
    },
  },
  /**
   * Use the extra reducers function
   * @param builder
   */
  extraReducers: (builder: ActionReducerMapBuilder<ConsumerChargePersonalInitialProperties>) => {
    builder
      .addCase(
        fetchConsumerChargePersonal.pending,
        (state: ConsumerChargePersonalInitialProperties) => {
          state.loading = true;
        },
      )
      .addCase(
        fetchConsumerChargePersonal.fulfilled,
        (
          state: ConsumerChargePersonalInitialProperties,
          action: PayloadAction<APIConsumerChargePersonalResponseInterface>,
        ) => {
          state.loading = false;
          state.consumers = [...action.payload.results];
          state.limit = action.payload.limit;
          state.page = action.payload.page;
          state.totalPages = action.payload.totalPages;
          state.totalResults = action.payload.totalResults;
        },
      )
      .addCase(
        fetchConsumerChargePersonal.rejected,
        (state: ConsumerChargePersonalInitialProperties) => {
          state.loading = false;
          // Handle error if needed
        },
      );

    builder
      .addCase(fetchConsumerChargePersonalFilter.pending, (state) => {
        state.filterListLoading = true;
      })
      .addCase(
        fetchConsumerChargePersonalFilter.fulfilled,
        (state, action: PayloadAction<CommonFilterProperties[]>) => {
          state.filterListLoading = false;
          state.filters = [...(state.filters || []), ...action.payload];
        },
      )
      .addCase(fetchConsumerChargePersonalFilter.rejected, (state) => {
        state.filterListLoading = false;
      });
  },
});

// Export actions
export const { updateConsumerChargePersonalListFilter, setSortData } =
  ConsumerChargePersonal.actions;
// Export the reducer
export default ConsumerChargePersonal.reducer;
